import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BookOpenText, LibraryBig } from "lucide-react";

import MainLayout from "@/components/MainLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { publicApi } from "@/lib/api";

export default function GlossaryPage({ session, onLogout }) {
  const [content, setContent] = useState(null);

  useEffect(() => {
    const load = async () => {
      const data = await publicApi.getContent();
      setContent(data);
    };
    load();
  }, []);

  const glossary = useMemo(() => (content?.legal || []).find((doc) => doc.slug === "begriffsverzeichnis"), [content]);

  const entries = useMemo(() => {
    if (!glossary?.content) return [];
    return glossary.content.split("\n\n").map((entry) => {
      const [term, ...rest] = entry.split(" — ");
      return { term: term?.trim(), definition: rest.join(" — ").trim() };
    }).filter((entry) => entry.term && entry.definition);
  }, [glossary]);

  return (
    <MainLayout
      session={session}
      onLogout={onLogout}
      pageTitle="Begriffsverzeichnis"
      pageDescription="Begriffsverzeichnis der wichtigsten Verbands-, Status- und Kennungsbegriffe von REEXPERTS."
    >
      <div className="bg-[#111111] text-[#f7f1e8]">
        <div className="mx-auto flex max-w-7xl flex-col gap-12 px-6 py-14 lg:px-8 lg:py-20">
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="grid gap-8 xl:grid-cols-[0.95fr_1.05fr]" data-testid="glossary-hero-section">
            <div className="space-y-6 rounded-[2rem] border border-[#d4af37]/25 bg-[#141414] p-8">
              <Badge className="w-fit rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#e5d3b3]" data-testid="glossary-hero-badge">
                <span className="mr-3 inline-flex items-center gap-1">
                  <span className="h-2 w-4 rounded-full bg-[#2f2f2f]" />
                  <span className="h-2 w-4 rounded-full bg-[#a8171b]" />
                  <span className="h-2 w-4 rounded-full bg-[#d4af37]" />
                </span>
                Verbindliches Begriffsverzeichnis
              </Badge>
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#b99d6b]" data-testid="glossary-hero-kicker">Verbandsbegriffe · Kennungen · Zustände</p>
                <h1 className="mt-4 font-serif text-4xl leading-[1.04] text-white lg:text-5xl" data-testid="glossary-hero-heading">
                  Das Begriffsverzeichnis der Vereinigung.
                </h1>
              </div>
              <p className="text-base leading-relaxed text-[#cbc2b7]" data-testid="glossary-hero-description">
                Das Begriffsverzeichnis definiert die wichtigsten Verbands-, Verfahrens-, Status- und Kennungsbegriffe der Vereinigung. Es dient der einheitlichen Sprachführung in öffentlicher und interner Kommunikation und bleibt zugleich intern editierbar.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/rechtliches" data-testid="glossary-hero-legal-link">
                  <Button className="rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]">Zur Rechtsseite</Button>
                </Link>
                <Link to="/faq" data-testid="glossary-hero-faq-link">
                  <Button variant="outline" className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5">Zur FAQ</Button>
                </Link>
              </div>
            </div>

            <Card className="border-white/10 bg-[#171717]" data-testid="glossary-intro-card">
              <CardContent className="space-y-4 p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#e5d3b3]">
                  <LibraryBig className="h-6 w-6" />
                </div>
                <p className="font-serif text-3xl text-white">Einheitliche Begriffe sichern Einheitlichkeit im gesamten System.</p>
                <p className="text-sm leading-relaxed text-[#d6cec3]">Begriffe wie REEXPERTS-Kennung, Aufnahmevorgang, Beschwerdevorgang, Beitragsvorgang oder Verwaltungsregister werden hier verbindlich im Anwendungszusammenhang erläutert.</p>
              </CardContent>
            </Card>
          </motion.section>

          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="glossary-entries-section">
            {entries.map((entry, index) => (
              <Card key={entry.term} className="border-white/10 bg-[#171717]" data-testid={`glossary-entry-card-${index + 1}`}>
                <CardContent className="space-y-4 p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#e5d3b3]">
                    <BookOpenText className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#b99d6b]">Begriff</p>
                    <h2 className="mt-2 font-serif text-[1.8rem] leading-tight text-white" data-testid={`glossary-entry-term-${index + 1}`}>{entry.term}</h2>
                  </div>
                  <p className="text-sm leading-relaxed text-[#d6cec3]" data-testid={`glossary-entry-definition-${index + 1}`}>{entry.definition}</p>
                </CardContent>
              </Card>
            ))}
          </section>
        </div>
      </div>
    </MainLayout>
  );
}
